import { useEffect, useRef } from "react";
import { Platform } from "@xt/xtbase";
import {
  registerAppWebviewReuse,
  setAppNativeTitle,
  setAppWebviewReuseTitle,
} from "@/utils/appWebviewReuse";

/**
 * App: sync native nav via dsBridge.setTitle (does not change browser tab title).
 * Browser: leave document.title to global Seo/CMS — same as /activity/voucher.
 *
 * @param {string} title Localized page title string.
 * @param {() => void | Promise<void>} [onReuse] Optional data refresh on WebView reuse.
 */
export default function useDocumentTitle(title, onReuse) {
  const onReuseRef = useRef(onReuse);

  useEffect(() => {
    onReuseRef.current = onReuse;
  }, [onReuse]);

  useEffect(() => {
    if (typeof window === "undefined" || !title || !Platform.isInApp()) {
      return undefined;
    }

    const cleanupTitle = setAppWebviewReuseTitle(title);
    let cleanupReuse;

    if (onReuseRef.current) {
      cleanupReuse = registerAppWebviewReuse(async () => {
        await onReuseRef.current?.();
      });
    }

    const reassertNativeTitle = () => setAppNativeTitle(title);
    window.addEventListener("visibilitychange", reassertNativeTitle);
    window.addEventListener("pageshow", reassertNativeTitle);

    let scrollTimer;
    const onScroll = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(reassertNativeTitle, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cleanupTitle();
      cleanupReuse?.();
      window.removeEventListener("visibilitychange", reassertNativeTitle);
      window.removeEventListener("pageshow", reassertNativeTitle);
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(scrollTimer);
    };
  }, [title]);
}
